import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import styles from '../../styles/AdminPanel.module.scss';
import { posterUrl } from '../../utils/imageUrl';
import { updatePoster } from '../../Store/posters/postersSlice';
import { downloadExcel } from '../../utils/exportExcel';

const FILTER_OPTIONS = [
  { id: 'all', label: 'Усі постери' },
  { id: 'active', label: 'Активні' },
  { id: 'hidden', label: 'Приховані' },
  { id: 'withProduct', label: 'С товаром' },
  { id: 'withImage', label: 'С изображением' },
];

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('uk-UA', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const exportPostersExcel = (items) => {
  const headers = ['ID', 'Заголовок', 'Підзаголовок', 'Назва', 'Кнопка', 'Порядок', 'Активний', 'Товар ID', 'Посилання'];
  const rows = items.map((p) => [
    p.id,
    p.title || '',
    p.subtitle || '',
    p.heading || '',
    p.buttonText || '',
    p.sortOrder ?? 0,
    p.isActive !== 0 ? 'Да' : 'Нет',
    p.productId || '',
    p.buttonLink || '',
  ]);
  downloadExcel(`posters-${new Date().toISOString().slice(0, 10)}`, 'Постери', headers, rows);
};

const PostersList = ({
  posters = [],
  products = [],
  loading,
  error,
  onEdit,
  onDelete,
  onCreate,
  onRefresh,
  showMessage,
}) => {
  const dispatch = useDispatch();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('sort');
  const [sortDir, setSortDir] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const items = Array.isArray(posters) ? posters : [];

  const productMap = useMemo(() => {
    const map = {};
    products.forEach((p) => { map[p.id] = p; });
    return map;
  }, [products]);

  const stats = useMemo(() => {
    const total = items.length;
    const active = items.filter((p) => p.isActive !== 0).length;
    const hidden = total - active;
    const withProduct = items.filter((p) => p.productId).length;
    return { total, active, hidden, withProduct };
  }, [items]);

  const filterCounts = useMemo(() => {
    const counts = { all: items.length };
    counts.active = items.filter((p) => p.isActive !== 0).length;
    counts.hidden = items.filter((p) => p.isActive === 0).length;
    counts.withProduct = items.filter((p) => p.productId).length;
    counts.withImage = items.filter((p) => p.image).length;
    return counts;
  }, [items]);

  const filteredPosters = useMemo(() => {
    const query = search.trim().toLowerCase();
    let list = items.filter((poster) => {
      const productTitle = productMap[poster.productId]?.title || '';
      const matchesSearch = !query || [
        poster.title,
        poster.subtitle,
        poster.heading,
        poster.buttonText,
        productTitle,
        String(poster.id),
      ].some((v) => String(v || '').toLowerCase().includes(query));

      if (!matchesSearch) return false;

      switch (filter) {
        case 'active':
          return poster.isActive !== 0;
        case 'hidden':
          return poster.isActive === 0;
        case 'withProduct':
          return Boolean(poster.productId);
        case 'withImage':
          return Boolean(poster.image);
        default:
          return true;
      }
    });

    list = [...list].sort((a, b) => {
      let cmp = 0;
      switch (sortBy) {
        case 'title':
          cmp = String(a.title).localeCompare(String(b.title), 'uk');
          break;
        case 'heading':
          cmp = String(a.heading).localeCompare(String(b.heading), 'uk');
          break;
        case 'date':
          cmp = new Date(a.updatedAt || a.createdAt || 0) - new Date(b.updatedAt || b.createdAt || 0);
          break;
        case 'sort':
        default:
          cmp = Number(a.sortOrder ?? 0) - Number(b.sortOrder ?? 0);
          if (cmp === 0) cmp = Number(a.id) - Number(b.id);
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return list;
  }, [items, search, filter, sortBy, sortDir, productMap]);

  const allSelected = filteredPosters.length > 0 && selected.length === filteredPosters.length;

  const toggleSelectAll = () => {
    setSelected(allSelected ? [] : filteredPosters.map((p) => p.id));
  };

  const toggleSelect = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  const handleExport = () => {
    const exportItems = selected.length
      ? filteredPosters.filter((p) => selected.includes(p.id))
      : filteredPosters;
    exportPostersExcel(exportItems);
  };

  const handleToggleActive = async (poster) => {
    setTogglingId(poster.id);
    try {
      const data = new FormData();
      data.append('title', poster.title || '');
      data.append('subtitle', poster.subtitle || '');
      data.append('heading', poster.heading || '');
      data.append('buttonText', poster.buttonText || '');
      data.append('productId', poster.productId || '');
      data.append('buttonLink', poster.buttonLink || '/');
      data.append('sortOrder', poster.sortOrder ?? 0);
      data.append('isActive', poster.isActive !== 0 ? '0' : '1');
      await dispatch(updatePoster({ id: poster.id, formData: data })).unwrap();
      showMessage?.('success', poster.isActive !== 0 ? 'Постер приховано' : 'Постер опубліковано');
      onRefresh?.();
    } catch (err) {
      showMessage?.('error', err || 'Не вдалося змінити статус');
    } finally {
      setTogglingId(null);
    }
  };

  if (!items.length) {
    return (
      <div className={styles.postersListPage}>
        <div className={styles.postersListHeader}>
          <h2 className={styles.postersListTitle}>Список постерів</h2>
          <button type="button" className={styles.postersListAddBtn} onClick={onCreate}>
            Створити постер
          </button>
        </div>
        <div className={styles.empty}>Постери отсутствуют</div>
      </div>
    );
  }

  return (
    <div className={styles.postersListPage}>
      <div className={styles.postersListHeader}>
        <h2 className={styles.postersListTitle}>Список постерів</h2>
        <div className={styles.postersListHeaderActions}>
          <button type="button" className={styles.postersListExportBtn} onClick={handleExport}>
            Експорт в Excel
          </button>
          {onRefresh && (
            <button
              type="button"
              className={styles.postersListRefreshBtn}
              onClick={onRefresh}
              disabled={loading}
            >
              {loading ? 'Завантаження…' : 'Оновити'}
            </button>
          )}
          <button type="button" className={styles.postersListAddBtn} onClick={onCreate}>
            Створити постер
          </button>
        </div>
      </div>

      {error && (
        <div className={`${styles.message} ${styles.error}`}>
          {typeof error === 'string' ? error : 'Не вдалося завантажити постери'}
        </div>
      )}

      <div className={styles.postersListStats}>
        <div className={styles.postersListStat}>
          <span className={styles.postersListStatLabel}>Усіго</span>
          <strong>{stats.total}</strong>
        </div>
        <div className={styles.postersListStat}>
          <span className={styles.postersListStatLabel}>Активні</span>
          <strong className={styles.postersListStatActive}>{stats.active}</strong>
        </div>
        <div className={styles.postersListStat}>
          <span className={styles.postersListStatLabel}>Приховані</span>
          <strong>{stats.hidden}</strong>
        </div>
        <div className={styles.postersListStat}>
          <span className={styles.postersListStatLabel}>С товаром</span>
          <strong>{stats.withProduct}</strong>
        </div>
      </div>

      <div className={styles.postersListToolbar}>
        <select
          className={styles.postersListFilterSelect}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          {FILTER_OPTIONS.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.label} ({filterCounts[opt.id] ?? 0})
            </option>
          ))}
        </select>
        <div className={styles.postersListSearch}>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Пошук за заголовком, назвою, кнопкою..."
          />
        </div>
      </div>

      <div className={styles.postersTableWrap}>
        <table className={styles.postersTable}>
          <thead>
            <tr>
              <th className={styles.postersTableCheck}>
                <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
              </th>
              <th />
              <th>
                <button type="button" className={styles.postersTableSort} onClick={() => toggleSort('title')}>
                  Заголовок {sortBy === 'title' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </button>
              </th>
              <th>
                <button type="button" className={styles.postersTableSort} onClick={() => toggleSort('heading')}>
                  Назва {sortBy === 'heading' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </button>
              </th>
              <th>
                <button type="button" className={styles.postersTableSort} onClick={() => toggleSort('sort')}>
                  Порядок {sortBy === 'sort' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </button>
              </th>
              <th>Відображення</th>
              <th>Посилання</th>
              <th>
                <button type="button" className={styles.postersTableSort} onClick={() => toggleSort('date')}>
                  Дата {sortBy === 'date' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </button>
              </th>
              <th>Дії</th>
            </tr>
          </thead>
          <tbody>
            {filteredPosters.length === 0 ? (
              <tr>
                <td colSpan={9} className={styles.postersTableEmpty}>
                  За вашим запитом нічого не знайдено
                </td>
              </tr>
            ) : (
              filteredPosters.map((poster) => {
                const linkedProduct = productMap[poster.productId];
                const isActive = poster.isActive !== 0;

                return (
                  <tr
                    key={poster.id}
                    className={selected.includes(poster.id) ? styles.postersTableSelected : ''}
                  >
                    <td className={styles.postersTableCheck}>
                      <input
                        type="checkbox"
                        checked={selected.includes(poster.id)}
                        onChange={() => toggleSelect(poster.id)}
                      />
                    </td>
                    <td className={styles.postersTablePreview}>
                      <div className={styles.postersTablePreviewBox}>
                        <span
                          className={`${styles.postersTableStatusBar} ${
                            isActive ? styles.postersTableStatusActive : styles.postersTableStatusHidden
                          }`}
                        />
                        {poster.image ? (
                          <img src={posterUrl(poster.image)} alt={poster.heading} />
                        ) : (
                          <span className={styles.postersTableNoImage}>—</span>
                        )}
                      </div>
                    </td>
                    <td className={styles.postersTableTitleCol}>
                      <button type="button" className={styles.postersTableTitle} onClick={() => onEdit(poster)}>
                        {poster.title || 'Без заголовка'}
                      </button>
                      <div className={styles.postersTableSubtitle}>{poster.subtitle || '—'}</div>
                      <div className={styles.postersTableTags}>
                        <span className={styles.postersTableTag}>{poster.buttonText || 'Кнопка'}</span>
                      </div>
                    </td>
                    <td className={styles.postersTableHeading}>{poster.heading || '—'}</td>
                    <td className={styles.postersTableSortOrder}>{poster.sortOrder ?? 0}</td>
                    <td className={styles.postersTableDisplay}>
                      <button
                        type="button"
                        className={isActive ? styles.posterStatusActive : styles.posterStatusHidden}
                        onClick={() => handleToggleActive(poster)}
                        disabled={togglingId === poster.id}
                      >
                        {togglingId === poster.id ? '…' : isActive ? 'На головній' : 'Приховано'}
                      </button>
                    </td>
                    <td className={styles.postersTableLink}>
                      {linkedProduct ? (
                        <span>Товар: {linkedProduct.title}</span>
                      ) : (
                        <span>{poster.buttonLink || '/'}</span>
                      )}
                    </td>
                    <td className={styles.postersTableDate}>
                      {formatDate(poster.updatedAt || poster.createdAt)}
                    </td>
                    <td className={styles.postersTableActions}>
                      <div className={styles.actionsMenu}>
                        <button
                          type="button"
                          className={styles.actionsMenuBtn}
                          onClick={() => setOpenMenuId(openMenuId === poster.id ? null : poster.id)}
                        >
                          Дії ▾
                        </button>
                        {openMenuId === poster.id && (
                          <div className={styles.actionsMenuList}>
                            <button type="button" onClick={() => { onEdit(poster); setOpenMenuId(null); }}>
                              Редагувати
                            </button>
                            <button type="button" onClick={() => { handleToggleActive(poster); setOpenMenuId(null); }}>
                              {isActive ? 'Приховати' : 'Показати'}
                            </button>
                            <button type="button" onClick={() => { onDelete(poster); setOpenMenuId(null); }}>
                              Видалити
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className={styles.postersListFooter}>
        Показано {filteredPosters.length} з {items.length}
        {selected.length > 0 && ` · Обрано: ${selected.length}`}
      </div>
    </div>
  );
};

export default PostersList;
